import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ACTIONS } from "../../_config";
import { useConsentApi } from "../../store/api/hooks/consentApiHook";
import { useUserManagementApi } from "../../store/api/hooks/userManagementApiHook";
import { useIsOwnerAdmin, useOwnerAdminFlag, useTermAcceptedFlag, useUserSelector } from "../../store/selectors/userSelector";
import Loader from "../loader";
import NotActivatedScreen from "./NotActivatedScreen";
import OwnerConsentModal from "./OwnerConsentModal";

/**
 * Blocks the whole app until the business has accepted the terms.
 *
 * Reads `isTermAccepted` and `isOwnerAdmin` from the login session. A session
 * saved before the consent release has neither, so each missing one is resolved
 * once: consent against `Consent/Merchant`, owner-admin against the user's role
 * (`RolePermission/Detail`). A loader shows while consent is unresolved; the
 * owner flag alone is fetched in the background. While locked the children are
 * NOT rendered at all (no menu, page or My Profile, and no data requests behind
 * the window); a consent error fails closed.
 */
const ConsentGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const consentApi = useConsentApi();
  const { getRolePermission } = useUserManagementApi();
  const user: any = useUserSelector();
  const isOwner = useIsOwnerAdmin();
  const flag = useTermAcceptedFlag();
  const ownerFlag = useOwnerAdminFlag();
  const [isResolving, setIsResolving] = useState(flag === undefined);
  const ownerAttempted = useRef(false);

  useEffect(() => {
    const needConsent = flag === undefined;
    const roleId = user?.roles?.[0]?.roleID;
    const needOwner = ownerFlag === undefined && !ownerAttempted.current && roleId != null;
    if (!needConsent && !needOwner) return;
    if (needConsent) setIsResolving(true);
    if (needOwner) ownerAttempted.current = true;

    // Both lookups run in parallel but write the session once, so neither overwrites the other's field.
    const patch: Record<string, boolean> = {};
    let pending = (needConsent ? 1 : 0) + (needOwner ? 1 : 0);
    const settle = () => {
      if (--pending > 0) return;
      if (Object.keys(patch).length) dispatch({ type: ACTIONS.USER.USER, payload: { ...user, ...patch } });
      setIsResolving(false);
    };

    if (needConsent) {
      consentApi.getMerchantConsent(
        {},
        (resp: any) => {
          patch.isTermAccepted = resp?.data?.accepted === true;
          settle();
        },
        () => {
          patch.isTermAccepted = false;
          settle();
        }
      );
    }
    if (needOwner) {
      getRolePermission(
        { id: roleId },
        (resp: any) => {
          patch.isOwnerAdmin = resp?.data?.items?.isOwnerAdmin === true;
          settle();
        },
        // Not persisted on error, so a transient failure doesn't pin the user as non-owner for the whole session.
        settle
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag, ownerFlag]);

  if (flag === true) return <>{children}</>;
  if (isResolving) {
    return (
      <div className="flex items-center justify-center w-screen h-screen bg-white">
        <Loader />
      </div>
    );
  }
  if (isOwner) {
    return (
      <div className="w-screen h-screen bg-gray-100">
        <OwnerConsentModal />
      </div>
    );
  }
  return <NotActivatedScreen />;
};

export default ConsentGate;
